import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrialSignupModal({ open, onClose }) {
  const [form, setForm] = useState({ company_name: "", owner_email: "", owner_name: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/api/leads/trial-signup`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      setErrorMsg(msg);
    }
  };

  const handleClose = () => {
    setStatus("idle");
    setForm({ company_name: "", owner_email: "", owner_name: "" });
    setErrorMsg("");
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="trial-signup-modal"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="relative w-full max-w-md bg-geminii-card border border-white/[0.08] rounded-2xl p-8 backdrop-blur-2xl shadow-2xl"
          >
            <button
              onClick={handleClose}
              data-testid="trial-modal-close"
              className="absolute top-4 right-4 text-geminii-dim hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            {status === "success" ? (
              <div className="text-center py-8" data-testid="trial-modal-success">
                <CheckCircle className="mx-auto mb-4 text-rogue-cyan" size={48} />
                <h3 className="font-display font-bold text-xl text-white mb-2">
                  You're in.
                </h3>
                <p className="text-geminii-muted text-sm">
                  We've received your request. We'll be in touch shortly with your login details.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-6 px-6 py-2.5 rounded-full bg-rogue-cyan text-geminii-deep text-sm font-semibold"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-display font-bold text-xl text-white mb-1">
                  Start your free trial
                </h3>
                <p className="text-geminii-muted text-sm mb-6">
                  No credit card required. Get started in under 2 minutes.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-geminii-muted mb-1.5 block">
                      Your name
                    </label>
                    <input
                      type="text"
                      required
                      value={form.owner_name}
                      onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                      data-testid="trial-input-name"
                      placeholder="Dirk van Schalkwyk"
                      className="w-full px-4 py-3 rounded-lg bg-geminii-deep border border-white/[0.08] text-white text-sm placeholder:text-geminii-dim focus:outline-none focus:border-rogue-cyan focus:ring-1 focus:ring-rogue-cyan transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-geminii-muted mb-1.5 block">
                      Work email
                    </label>
                    <input
                      type="email"
                      required
                      value={form.owner_email}
                      onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                      data-testid="trial-input-email"
                      placeholder="you@company.com"
                      className="w-full px-4 py-3 rounded-lg bg-geminii-deep border border-white/[0.08] text-white text-sm placeholder:text-geminii-dim focus:outline-none focus:border-rogue-cyan focus:ring-1 focus:ring-rogue-cyan transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-geminii-muted mb-1.5 block">
                      Company name
                    </label>
                    <input
                      type="text"
                      required
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      data-testid="trial-input-company"
                      placeholder="Hidroverse"
                      className="w-full px-4 py-3 rounded-lg bg-geminii-deep border border-white/[0.08] text-white text-sm placeholder:text-geminii-dim focus:outline-none focus:border-rogue-cyan focus:ring-1 focus:ring-rogue-cyan transition-all"
                    />
                  </div>

                  {status === "error" && (
                    <div
                      data-testid="trial-modal-error"
                      className="flex items-center gap-2 text-rogue-rose text-xs bg-rogue-rose/10 rounded-lg px-3 py-2"
                    >
                      <AlertCircle size={14} />
                      {errorMsg}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="trial-submit-btn"
                    className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-rogue-cyan text-geminii-deep text-sm font-semibold hover:shadow-[0_0_20px_rgba(31,182,193,0.4)] transition-all duration-250 disabled:opacity-50"
                  >
                    {status === "loading" ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <>
                        Start free trial
                        <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
